import { createPopper, type Placement } from '@popperjs/core';
import classnames from 'classnames';
import {
  Children,
  cloneElement,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
  type HTMLProps,
  type ReactElement,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefAttributes,
} from 'react';

import { PopoverLayover } from './PopoverLayover';
import { PopoverTrigger, type PopoverTriggerProps } from './PopoverTrigger';

import styles from './Popover.module.scss';

export { PopoverLayover, PopoverTrigger, type PopoverTriggerProps };

export type FallbackPosition = 'bottom' | 'left' | 'right' | 'top';

export interface PopoverProps
  extends Omit<HTMLProps<HTMLDivElement>, 'className'> {
  /**
   * To override the style for popover arrow
   */
  arrowClassName?: classnames.Argument;
  /**
   * To override the style for popover
   */
  className?: classnames.Argument;
  /**
   * To override the style for popover content
   */
  contentClassName?: classnames.Argument;
  /**
   * To define the fallback position for Popover
   */
  fallbackPosition?: FallbackPosition;
  /**
   * Whether to show the closeButton or not
   */
  hiddenCloseButton?: boolean;
  /**
   * To define the space between children and Popover
   */
  isFixed?: boolean;
  /**
   * Whether to show the popover or not
   */
  isVisible?: boolean;
  /**
   * To define the position for Popover
   */
  position?: Placement;
  /**
   * To define the title for the Popover
   */
  onClose(event: KeyboardEvent | MouseEvent | TouchEvent): unknown;
}

/**
 * A Popover can be used to display some content on top of another.
 */
export const Popover: FunctionComponent<PopoverProps> = ({
  arrowClassName,
  children,
  className,
  contentClassName,
  fallbackPosition = 'bottom',
  hiddenCloseButton = false,
  isFixed = false,
  isVisible = false,
  onClose,
  position = 'bottom',
  ...restProps
}) => {
  // Allows waiting for the popper styles to be added before making the popover
  // visible.
  const [hidden, setHidden] = useState(true);
  const triggerRef = useRef<HTMLElement>(null);
  const arrowRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const childrenArray = Children.toArray(children);

  const trigger = childrenArray.find(
    (child) =>
      (child as ReactElement<unknown, FunctionComponent>)?.type.displayName ===
      'PopoverTrigger',
  ) as
    | ReactElement<RefAttributes<HTMLElement>, typeof PopoverTrigger>
    | undefined;

  const content = childrenArray.find(
    (child) =>
      (child as ReactElement<unknown, FunctionComponent>)?.type.displayName ===
      'PopoverLayover',
  ) as ReactElement<PopoverTriggerProps, typeof PopoverLayover> | undefined;

  const popoverArrowClassName = classnames(styles.arrow, arrowClassName, {
    [styles.arrowVisible]: isVisible,
  });

  const onKeypressClose = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      onClose(event.nativeEvent);
    }
  };

  useEffect(() => {
    if (isVisible) {
      const onClickOutside = (event: MouseEvent | TouchEvent) => {
        if (!popupRef.current?.contains(event.target as Node)) {
          onClose(event);
        }
      };

      document.addEventListener('mousedown', onClickOutside);
      document.addEventListener('touchstart', onClickOutside);

      return () => {
        document.removeEventListener('mousedown', onClickOutside);
        document.removeEventListener('touchstart', onClickOutside);
      };
    }
  }, [isVisible, onClose]);

  useEffect(() => {
    if (!triggerRef.current || !popupRef.current) {
      return;
    }

    const allPositions = ['bottom', 'left', 'top', 'right'].filter(
      (pos) => pos !== fallbackPosition,
    );

    const popper = createPopper(triggerRef.current, popupRef.current, {
      placement: position,
      strategy: isFixed ? 'fixed' : 'absolute',
      modifiers: [
        {
          name: 'offset',
          options: { offset: [0, 8] },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: [fallbackPosition, ...allPositions],
          },
        },
        {
          name: 'arrow',
          options: { element: arrowRef.current },
        },
      ],
    });
    setHidden(false);

    return () => {
      popper.destroy();
      setHidden(true);
    };
  }, [fallbackPosition, isFixed, isVisible, position]);

  return (
    <>
      {trigger && cloneElement(trigger, { ref: triggerRef })}
      {isVisible && (
        <div
          className={classnames(
            styles.popover,
            { [styles.hidden]: hidden },
            className,
          )}
          ref={popupRef}
          {...restProps}
        >
          <div className={classnames(styles.popoverContent, contentClassName)}>
            <div className={styles.popoverChildren}>{content}</div>
            {!hiddenCloseButton && (
              <i
                className={styles.close}
                tabIndex={0}
                role="button"
                aria-label="close"
                onClick={(event) => onClose(event.nativeEvent)}
                onKeyPress={onKeypressClose}
              />
            )}
          </div>
          <div
            className={popoverArrowClassName}
            ref={arrowRef}
            data-popper-arrow
          />
        </div>
      )}
    </>
  );
};
